import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { getClientMessagesList, getClientMessages, sendClientMessage } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtMsgTime(d) {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtFullTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Thread List Panel ────────────────────────────────────────────────────────
function ThreadList({ threads, selectedArCode, onSelect, loading }) {
  const [search, setSearch] = useState('');

  const filtered = threads.filter(t => {
    const q = search.toLowerCase();
    return (
      t.client_name?.toLowerCase().includes(q) ||
      t.ar_code?.toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{
      width: '30%',
      minWidth: 240,
      borderRight: '1px solid #E4E8EF',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#fff',
    }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #E4E8EF' }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, color: '#0D2C54', fontSize: '1rem' }}>
          Client Messages
        </Typography>
        <TextField
          size="small"
          placeholder="Search clients…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#9aa5b4' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F5F7FA',
            },
          }}
        />
      </Box>

      {/* Thread list */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: '#9aa5b4' }}>
            <ChatIcon sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
            <Typography variant="body2">
              {search ? 'No clients match your search' : 'No message threads yet'}
            </Typography>
          </Box>
        ) : (
          filtered.map((thread) => (
            <Box
              key={thread.ar_code}
              onClick={() => onSelect(thread)}
              sx={{
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                borderBottom: '1px solid #F0F2F5',
                bgcolor: selectedArCode === thread.ar_code ? '#EEF3FB' : 'transparent',
                borderRight: selectedArCode === thread.ar_code ? '3px solid #1A5490' : '3px solid transparent',
                transition: 'background 0.15s',
                '&:hover': {
                  bgcolor: selectedArCode === thread.ar_code ? '#EEF3FB' : '#F8F9FC',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: '#0D2C54',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {thread.client_name?.charAt(0)?.toUpperCase() || '?'}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={thread.unread_count > 0 ? 700 : 500}
                      sx={{ color: '#1a2535', lineHeight: 1.3 }}
                      noWrap
                    >
                      {thread.client_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9aa5b4' }} noWrap>
                      {thread.ar_code}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#9aa5b4', whiteSpace: 'nowrap' }}>
                    {fmtMsgTime(thread.last_message_date)}
                  </Typography>
                  {thread.unread_count > 0 && (
                    <Box sx={{
                      bgcolor: '#c62828',
                      color: '#fff',
                      borderRadius: '10px',
                      minWidth: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: 700,
                      px: 0.75,
                    }}>
                      {thread.unread_count}
                    </Box>
                  )}
                </Box>
              </Box>
              {thread.last_message_preview && (
                <Typography
                  variant="caption"
                  sx={{
                    color: thread.unread_count > 0 ? '#1a2535' : '#9aa5b4',
                    fontWeight: thread.unread_count > 0 ? 600 : 400,
                    mt: 0.5,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    pl: '44px', // align under the name
                  }}
                >
                  {thread.last_message_direction === 'outbound' ? 'You: ' : ''}
                  {thread.last_message_preview}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

// ─── Thread View Panel ────────────────────────────────────────────────────────
function ThreadView({ selectedThread, messages, loading, onSend }) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      scrollToBottom();
    }
  }, [messages, loading, scrollToBottom]);

  const handleSend = async () => {
    const trimmed = replyText.trim();
    if (!trimmed || sending || !selectedThread) return;
    setSending(true);
    try {
      await onSend(selectedThread.ar_code, trimmed);
      setReplyText('');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!selectedThread) {
    return (
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F8F9FC',
        color: '#9aa5b4',
        gap: 1,
      }}>
        <ChatIcon sx={{ fontSize: 48, opacity: 0.3 }} />
        <Typography variant="body1" sx={{ fontWeight: 500, color: '#5a6a7e' }}>
          Select a client to view their messages
        </Typography>
        <Typography variant="body2" sx={{ color: '#9aa5b4' }}>
          Choose a conversation from the left panel
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Thread header */}
      <Box sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid #E4E8EF',
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}>
        <Box sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: '#0D2C54',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          fontWeight: 700,
          flexShrink: 0,
        }}>
          {selectedThread.client_name?.charAt(0)?.toUpperCase() || '?'}
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2535', lineHeight: 1.2 }}>
            {selectedThread.client_name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9aa5b4' }}>
            Account {selectedThread.ar_code}
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        bgcolor: '#F8F9FC',
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#9aa5b4' }}>
            <ChatIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">No messages yet. Send the first message below.</Typography>
          </Box>
        ) : (
          messages.map((msg) => {
            // direction = 'inbound' → client sent → show LEFT
            // direction = 'outbound' → ACS sent → show RIGHT
            const isACS = msg.direction === 'outbound';
            return (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isACS ? 'flex-end' : 'flex-start',
                  maxWidth: '72%',
                  alignSelf: isACS ? 'flex-end' : 'flex-start',
                }}
              >
                <Box sx={{
                  bgcolor: isACS ? '#1A5490' : '#fff',
                  color: isACS ? '#fff' : '#1a2535',
                  borderRadius: isACS ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  px: 2,
                  py: 1.25,
                  fontSize: '14px',
                  lineHeight: 1.5,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  wordBreak: 'break-word',
                  border: isACS ? 'none' : '1px solid #E4E8EF',
                }}>
                  {msg.message}
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.5,
                  flexDirection: isACS ? 'row-reverse' : 'row',
                }}>
                  <Typography variant="caption" sx={{ color: '#9aa5b4' }}>
                    {msg.author_name || (isACS ? 'ACS Team' : 'Client')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#c4cdd8' }}>·</Typography>
                  <Tooltip title={fmtFullTime(msg.created_at)}>
                    <Typography variant="caption" sx={{ color: '#9aa5b4', cursor: 'default' }}>
                      {fmtMsgTime(msg.created_at)}
                    </Typography>
                  </Tooltip>
                  {isACS && msg.read_at && (
                    <>
                      <Typography variant="caption" sx={{ color: '#c4cdd8' }}>·</Typography>
                      <Typography variant="caption" sx={{ color: '#9aa5b4', fontStyle: 'italic' }}>
                        Read
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            );
          })
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Reply input */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid #E4E8EF',
        bgcolor: '#fff',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-end',
      }}>
        <TextField
          multiline
          maxRows={4}
          fullWidth
          size="small"
          placeholder={`Reply to ${selectedThread.client_name}… (Enter to send, Shift+Enter for new line)`}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              fontSize: '14px',
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!replyText.trim() || sending}
          sx={{
            bgcolor: replyText.trim() && !sending ? '#1A5490' : '#E4E8EF',
            color: replyText.trim() && !sending ? '#fff' : '#9aa5b4',
            '&:hover': {
              bgcolor: replyText.trim() && !sending ? '#0D2C54' : '#E4E8EF',
            },
            transition: 'background 0.2s',
          }}
        >
          {sending ? <CircularProgress size={20} sx={{ color: '#9aa5b4' }} /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientMessages() {
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => {
    loadThreads();
  }, []);

  const loadThreads = async () => {
    try {
      const res = await getClientMessagesList();
      setThreads(res.data.threads || []);
    } catch (err) {
      console.error('Failed to load message threads:', err);
    } finally {
      setThreadsLoading(false);
    }
  };

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    setMessagesLoading(true);
    setMessages([]);
    try {
      const res = await getClientMessages(thread.ar_code);
      setMessages(res.data.messages || []);
      // Mark unread count as 0 in local thread list
      setThreads(prev =>
        prev.map(t =>
          t.ar_code === thread.ar_code ? { ...t, unread_count: 0 } : t
        )
      );
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSend = async (arCode, message) => {
    const res = await sendClientMessage(arCode, message);
    const newMsg = res.data.message;
    setMessages(prev => [...prev, newMsg]);
    // Update thread list preview
    setThreads(prev =>
      prev.map(t =>
        t.ar_code === arCode
          ? {
              ...t,
              last_message_preview: message.substring(0, 100),
              last_message_date: newMsg.created_at,
              last_message_direction: 'outbound',
            }
          : t
      )
    );
  };

  // Total unread for page title
  const totalUnread = threads.reduce((sum, t) => sum + (t.unread_count || 0), 0);

  useEffect(() => {
    document.title = totalUnread > 0
      ? `(${totalUnread}) Client Messages — ACS`
      : 'Client Messages — ACS';
    return () => { document.title = 'ACS Command Center'; };
  }, [totalUnread]);

  return (
    <Box sx={{ height: 'calc(100vh - 128px)', display: 'flex', borderRadius: 2, overflow: 'hidden', border: '1px solid #E4E8EF', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <ThreadList
        threads={threads}
        selectedArCode={selectedThread?.ar_code}
        onSelect={handleSelectThread}
        loading={threadsLoading}
      />
      <ThreadView
        selectedThread={selectedThread}
        messages={messages}
        loading={messagesLoading}
        onSend={handleSend}
      />
    </Box>
  );
}
